import AccountContext from 'context/AccountContext';
import { useContext } from 'react';

export type AnprPaymentOption =
  | 'TOKENIZED_PAYMENT_OPTION'
  | 'CORPORATE_ACCOUNT'
  | 'COMPANY_POOL'
  | 'NO_OPTIONS';

// Returns the payment option that is most widely usable for ANPR parking. Used
// on certain screens to determine if the user's only available payment option
// can only be used in specific locations (e.g. a company pool).
// If this hook returns TOKENIZED_PAYMENT_OPTION or CORPORATE_ACCOUNT, no
// warnings are needed. However, for the actual parking session we may still
// use a more limited option such as a company pool.
const useGetAvailableAnprPaymentOption = (): AnprPaymentOption => {
  const {
    customer: { paymentCards, companyBenefits, permits, paymentOptions },
  } = useContext(AccountContext);

  const hasActiveCard = !!paymentCards?.some(
    (card) => card.status === 'Active'
  );

  const hasTokenizedPaymentOption = paymentOptions?.some(
    (option) => option.isDefaultTokenizedPaymentOption
  );

  const isCorporateAccount = companyBenefits?.find(
    (benefit) => benefit.benefitTargetType === 'USE_CORPORATE_ACCOUNT'
  );
  const hasCompanyPayPool = permits?.some((permit) =>
    permit?.accessPermitPoolingGroupInfo?.some(
      (poolingGroup) =>
        poolingGroup?.insidePool !== 'USER_PAYS' &&
        poolingGroup?.outsidePool !== 'USER_PAYS'
    )
  );

  if (hasActiveCard || hasTokenizedPaymentOption)
    return 'TOKENIZED_PAYMENT_OPTION';
  if (isCorporateAccount) return 'CORPORATE_ACCOUNT';
  if (hasCompanyPayPool) return 'COMPANY_POOL';

  return 'NO_OPTIONS';
};

export default useGetAvailableAnprPaymentOption;
